import moment from "moment";
import { map, path } from "ramda";
import React, { useState, useEffect, useMemo } from "react";
import { Alert, Card } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import FormikContainer from "../../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../../components/modal/DeleteConfirmationModal.jsx";
import genericTranslations from "../../../../utils/genericTranslations.js";
import { selectBroadcastById } from "../../selectors.js";
import {
  fetchBroadcastById,
  fetchContentItemsByIds,
  fetchFolders,
  removeBroadcast,
  saveBroadcast,
} from "../../thunks.js";

import BroadcastForm from "./BroadcastForm.jsx";

const messages = defineMessages({
  couldNotFindBroadcast: {
    id: "modules.Arrivideo.components.BroadcastsDetail.couldNotFindBroadcast",
    description: "Could not find broadcast label.",
    defaultMessage: "Could not find broadcast.",
  },
  broadcast: {
    id: "modules.ArriVideo.components.BroadcastsDetail.broadcast",
    description: "broadcast label",
    defaultMessage: "Broadcast",
  },
  connectContentItem: {
    id: "modules.ArriVideo.components.BroadcastsDetail.connectContentItem",
    description: "1 content item connected label",
    defaultMessage: "At least 1 content item should be paired",
  },
  connectAudience: {
    id: "modules.ArriVideo.components.BroadcastsDetail.connectAudience",
    description: "1 audience connected label",
    defaultMessage: "At least 1 audience should be paired",
  },
  deleteBroadcast: {
    id: "modules.ArriVideo.components.BroadcastsDetail.deleteBroadcast",
    description: "Delete broadcast label",
    defaultMessage: "Delete broadcast",
  },
  emailNotValid: {
    id: "modules.ArriVideo.components.BroadcastsDetail.emailNotValid",
    description: "Not valid email address label",
    defaultMessage: "This email address is not valid",
  },
  actionNotValid: {
    id: "modules.ArriVideo.components.BroadcastsDetail.actionNotValid",
    description: "Require content in field label",
    defaultMessage: "When an action is selected, this input-field is required.",
  },
  urlNotValid: {
    id: "modules.ArriVideo.components.BroadcastsDetail.urlNotValid",
    description: "Not valid URL label",
    defaultMessage: "When a url is selected, http:// or https:// is required.",
  },
  phoneNotValid: {
    id: "modules.ArriVideo.components.BroadcastsDetail.phoneNotValid",
    description: "Not valid phone number label",
    defaultMessage:
      "When a phone number is selected, only the following characters are allowed: 0-9 +",
  },
  notificationWarning: {
    id: "modules.ArriVideo.components.BroadcastsDetail.notificationWarning",
    description: "Notification warning label",
    defaultMessage:
      "A notification text is inserted,  are you sure you don't want to send this notification?",
  },
  dateError: {
    id: "modules.ArriVideo.components.BroadcastsDetail.dateError",
    description: "Date Error label",
    defaultMessage: 'The date cannot be further in the future than "to" ',
  },
  deleteBroadcastConfirmation: {
    id: "modules.ArriVideo.components.BroadcastsDetail.deleteBroadcastConfirmation",
    description: "Delete Broadcast message",
    defaultMessage: "Are you sure you want to delete broadcast {broadcast}?",
  },
});

const BroadcastEditor = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { identifier } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const broadcast = useSelector((state) =>
    selectBroadcastById(state, { identifier }),
  );

  useEffect(() => {
    if (!identifier) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        await dispatch(fetchFolders());
        const broadcast = await dispatch(fetchBroadcastById(identifier));
        const contentItemIds = map((c) => c.id, broadcast.content || []);

        if (contentItemIds.length) {
          await dispatch(fetchContentItemsByIds(contentItemIds));
        }
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dispatch, identifier]);

  const validate = (values) => {
    const errors = {};
    const required = ["title", "from", "to", "folder"];
    required.forEach((k) => {
      if (!values[k])
        errors[k] = intl.formatMessage(genericTranslations.required);
    });

    if (moment(values.from).diff(moment(values.to)) > 0) {
      errors.from = intl.formatMessage(messages.dateError);
    }

    if (values.actionType && !values.actionContent) {
      errors.action = intl.formatMessage(messages.actionNotValid);
    }

    if (values.actionType && values.actionContent) {
      if (
        values.actionType === "site" &&
        !/^https?:\/\//.test(values.actionContent)
      ) {
        errors.action = intl.formatMessage(messages.urlNotValid);
      }

      if (
        values.actionType === "call" &&
        !/[\s\d+]+/.test(values.actionContent)
      ) {
        errors.action = intl.formatMessage(messages.phoneNotValid);
      }

      if (
        values.actionType === "mail" &&
        !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          values.actionContent,
        )
      ) {
        errors.action = intl.formatMessage(messages.emailNotValid);
      }
    }

    if (values.notificationText && !values.notification) {
      errors.notification = intl.formatMessage(messages.notificationWarning);
    }

    if (!values.contentItems || values.contentItems.length === 0) {
      errors.contentItems = intl.formatMessage(messages.connectContentItem);
    }

    if (!values.audiences || values.audiences.length === 0) {
      errors.audiences = intl.formatMessage(messages.connectAudience);
    }

    return errors;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    try {
      await dispatch(
        saveBroadcast(values, broadcast ? broadcast.id : undefined),
      );
      navigate("/arrivideo", { replace: true });
    } catch (err) {
      setError(
        `Er ging iets fout bij het opslaan: ${path(["response", "data", "message"], err) || err}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = useMemo(
    () => ({
      ...broadcast,
      audiences: (broadcast.audiences || []).map((audience) => ({
        label: audience.displayName,
        value: {
          id: audience.principalId,
          resourceType: audience.principalType, // specific for react-select
        },
      })),
    }),
    [broadcast],
  );

  const handleRemoveBroadcast = async () => {
    try {
      await dispatch(removeBroadcast(broadcast.id));
      navigate("/arrivideo", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    }
  };

  const dismissError = () => setError(undefined);

  if (loading) {
    return (
      <span className="align-middle">
        {intl.formatMessage(genericTranslations.loadingInProgress)}
      </span>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" onClose={dismissError} dismissible>
        <Alert.Heading>
          {intl.formatMessage(genericTranslations.error)}
        </Alert.Heading>
        <p>{error.toString()}</p>
      </Alert>
    );
  }

  if (!broadcast && identifier) {
    return (
      <strong>{intl.formatMessage(messages.couldNotFindBroadcast)}</strong>
    );
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        {intl.formatMessage(messages.broadcast)}{" "}
        {broadcast
          ? intl.formatMessage(genericTranslations.edit)
          : intl.formatMessage(genericTranslations.create)}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={initialValues || {}}
          onSubmit={handleSubmit}
          validate={validate}
          formComponent={BroadcastForm}
          extraProps={{
            onClickDelete: () => {
              setShowDeleteModal(true);
            },
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={intl.formatMessage(messages.deleteBroadcast)}
          bodyText={intl.formatMessage(messages.deleteBroadcastConfirmation, {
            broadcast: broadcast.title,
          })}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveBroadcast}
        />
      </Card.Body>
    </Card>
  );
};

export default BroadcastEditor;
